import {
	createManualGroup,
	addItemToGroup,
	removeItemFromGroup,
	addChildGroup,
	removeChildGroup,
} from "../db/groups.js";
import { showCreateGroupModal } from "../components/group-create-modal.js";
import { showToast } from "../components/toast.js";
// `navigate` lives in main.js, which wires up window-level listeners at import
// time. It's imported lazily (only when a "create" drop actually navigates) so
// this module's pure helpers stay importable outside the browser (e.g. tests).

// Decide what a drop means from the source/target kinds (pure, so it's testable):
//   item → item   create a new group from the two
//   item → group  add the item to the group
//   group → group nest the source group under the target
// Anything else (incl. dropping onto itself) is a no-op.
export function dropAction({ sourceId, sourceKind, targetId, targetKind }) {
	if (!sourceId || !targetId || sourceId === targetId) return "none";
	if (sourceKind === "item" && targetKind === "item") return "create";
	if (sourceKind === "item" && targetKind === "group") return "add";
	if (sourceKind === "group" && targetKind === "group") return "nest";
	return "none";
}

// A starting name suggestion for a group made from two items: their shared
// franchise/series if they have one, else empty (let the user type).
export function suggestGroupName(a, b) {
	if (a?.franchise && a.franchise === b?.franchise) return a.franchise;
	if (a?.collection && a.collection === b?.collection) return a.collection;
	return "";
}

// Run a row drop. `allMedia` is the normalized cross-media list (to look up the
// dropped items); `refresh` re-renders the current list after a change.
export async function handleRowDrop(payload, { allMedia, refresh }) {
	const action = dropAction(payload);
	const { sourceId, targetId } = payload;

	if (action === "create") {
		const a = allMedia.find((m) => m.id === sourceId);
		const b = allMedia.find((m) => m.id === targetId);
		// Same media type → scope the group to it; mixed → cross-media (null).
		const media_type =
			a && b && a.media_type === b.media_type ? a.media_type : null;
		const name = await showCreateGroupModal({
			suggestedName: suggestGroupName(a, b),
		});
		if (!name) return;
		const group = await createManualGroup({
			name,
			member_ids: [sourceId, targetId],
			media_type,
		});
		const { navigate } = await import("../main.js");
		navigate(`/groups/${group.id}`);
		return;
	}

	if (action === "add") {
		await addItemToGroup(targetId, sourceId);
		await refresh?.();
		const item = allMedia.find((m) => m.id === sourceId);
		showToast(`Added ${item?.title ?? "item"} to the group`, {
			actionLabel: "Undo",
			onAction: async () => {
				await removeItemFromGroup(targetId, sourceId);
				await refresh?.();
			},
		});
		return;
	}

	if (action === "nest") {
		try {
			await addChildGroup(targetId, sourceId);
		} catch (err) {
			showToast(err.message);
			return;
		}
		await refresh?.();
		showToast("Nested as a subgroup", {
			actionLabel: "Undo",
			onAction: async () => {
				await removeChildGroup(targetId, sourceId);
				await refresh?.();
			},
		});
	}
}
